import utils, {
  getArrayLength,
  isSingleElementArray,
  mapValueToMoneyFormat,
} from 'utils';
import { PriceRangeValue } from '../RangeFilterWrapper.component';
import { MAX_VALUE } from 'data/global';

export type RangeOptionsCopies = {
  UNDER_MIN: string;
  BETWEEN_VALUES: string;
  ABOVE_MAX: string;
};

export const translateInputToSelectValues = (
  from: string | number,
  to: string | number,
  minSelectValue: number,
  maxSelectValue: number,
  rangeCopies: RangeOptionsCopies,
  selectToInput: Record<string, PriceRangeValue>,
): PriceRangeValue[] => {
  const key =
    +from === maxSelectValue
      ? rangeCopies.ABOVE_MAX
      : +from === minSelectValue && +to === maxSelectValue
      ? rangeCopies.BETWEEN_VALUES
      : +to === minSelectValue
      ? rangeCopies.UNDER_MIN
      : '';

  if (!key || !selectToInput[key]) return [];

  return [selectToInput[key]];
};

export const createText = ({ from, to }: PriceRangeValue) => {
  const fromText = from ? mapValueToMoneyFormat(from) : '';
  const toText = to ? mapValueToMoneyFormat(to) : '';

  if (from && to && +to !== MAX_VALUE)
    return fromText === toText ? fromText : `${fromText}-${toText}`;

  return from ? `Above ${fromText}` : `Under ${toText}`;
};

export const getRangesFromParams = (
  from: number | string,
  to: number | string,
) => {
  const rangesFrom = utils.mapParamsToArray(from?.toString()) || [];
  const rangesTo = utils.mapParamsToArray(to?.toString()) || [];

  return rangesFrom.reduce<PriceRangeValue[]>(
    (acc, from, index) => [...acc, { from: +from, to: +rangesTo[index] }],
    [],
  );
};

export const getApiRangesFromParams = (
  from: number | string,
  to: number | string,
) => {
  const rangesFrom = utils.mapParamsToArray(from?.toString()) || [];
  const rangesTo = utils.mapParamsToArray(to?.toString()) || [];

  return rangesFrom.reduce<PriceRangeValue[]>(
    (acc, from, index) => [
      ...acc,
      {
        from: +from,
        ...(+rangesTo[index] !== MAX_VALUE && { to: +rangesTo[index] }),
      },
    ],
    [],
  );
};

export const getParamsFromRanges = (ranges: PriceRangeValue[] = []) => {
  const fromRanges = ranges.map(({ from }) => from.toString());
  const toRanges = ranges.map(({ to }) => to.toString());

  return {
    from: utils.mapArrayToParams(fromRanges),
    to: utils.mapArrayToParams(toRanges),
  };
};

export const mergeRanges = (ranges: PriceRangeValue[]): PriceRangeValue[] => {
  if (isSingleElementArray(ranges)) return ranges;

  const sorted = [...ranges].sort((a, b) => +a.from - +b.from);

  return sorted.reduce((merged, current) => {
    const { from, to } = current;

    const last = merged[getArrayLength(merged) - 1];
    const lastTo = +last?.to;

    if (+from <= lastTo) last.to = Math.max(lastTo, +to);
    else merged.push({ ...current });

    return merged;
  }, []);
};

export const getSelectValueFromRanges = (
  ranges: PriceRangeValue[] = [],
  rangeToSelect: Record<string, PriceRangeValue>,
) => {
  return ranges.map(
    ({ from, to }) =>
      Object.entries(rangeToSelect).find(
        ([_, value]) => +value.from === +from && +value.to === +to,
      )?.[0],
  );
};
